/**
 * Compact search (CHANGE 2026-10-18 §1.3) — home and room pages. Deliberately light: at page load it only
 * renders a « dates · guests » button and « Rechercher » from the page labels; it calls no API and loads no
 * calendar. Opening the button sends POST /start at once and loads the panel chunk (guests + calendar,
 * compact-panel.js) in parallel; the chunks are fetched ahead on intent (pointer, focus, touch) and when the
 * browser is idle — chunks only, never an API call before the click. « Rechercher » goes to the booking page
 * with the choices in the URL.
 */
import { h, clear } from './dom.js';
import { bookingUrl, datesLabel } from './stay-ui.js';
import { track } from './analytics.js';

/**
 * @param {Element} node Block mount point.
 * @param {{i18n: Object, msg: Object, navigate: Function, getBooking: () => Promise<Object>, loadPanel: () => Promise<Object>}} ctx
 * @param {{target: string, room: number|null, month?: string|null}} opts month = the shortcode's mois (0.9.6).
 */
export function mountCompact(node, ctx, { target, room, month = null }) {
	const { i18n } = ctx;
	let booking = null;
	let panel = null;
	const label = h('span', { class: 'pap-dates-trigger-l' });
	const trigger = h('button', { type: 'button', class: 'pap-dates-trigger', 'aria-haspopup': 'dialog', 'aria-expanded': 'false', on: { click: () => open() } }, label);
	const submit = h('button', { type: 'submit', class: 'pap-btn pap-search-submit', disabled: true }, i18n.t('ui.search'));
	const slot = h('div', { class: 'pap-compact-panel' });
	const form = h('form', { class: 'pap-compact', novalidate: true, on: { submit: (e) => { e.preventDefault(); go(); } } },
		h('div', { class: 'pap-picker' }, trigger, slot), submit);

	/** Fetch the lazy chunks ahead (no API call). */
	let warmed = false;
	function warm() {
		if (!warmed) {
			warmed = true;
			ctx.loadPanel().catch(() => {});
			ctx.getBooking().catch(() => {});
		}
	}
	for (const type of ['pointerenter', 'focusin', 'touchstart']) {
		form.addEventListener(type, warm, { once: true, passive: true });
	}
	(window.requestIdleCallback || ((fn) => setTimeout(fn, 2000)))(warm, { timeout: 3000 });

	function update() {
		const s = booking?.state;
		clear(label).append(
			s ? datesLabel({ booking, i18n, msg: ctx.msg }) : i18n.t('ui.datesButton'),
			i18n.sep(' · '),
			i18n.t('ui.guests', [s ? booking.guests() : 2], s ? booking.guests() : 2)
		);
		submit.disabled = !(s && s.arrival && s.departure);
	}

	async function open() {
		track('compact-ouvert');
		trigger.setAttribute('aria-busy', 'true');
		ctx.prefetchStart?.(); // POST /start leaves now, in parallel with the chunks
		const [module, b] = await Promise.all([ctx.loadPanel(), ctx.getBooking()]);
		trigger.removeAttribute('aria-busy');
		if (!booking) {
			booking = b;
			booking.subscribe(update);
		}
		booking.start();
		panel ??= module.createCompactPanel({ booking, i18n, msg: ctx.msg, mount: slot, anchor: form, month, onClose: () => { trigger.setAttribute('aria-expanded', 'false'); trigger.focus(); } });
		trigger.setAttribute('aria-expanded', 'true');
		panel.open();
	}

	function go() {
		const s = booking?.state;
		if (s?.arrival && s?.departure) {
			ctx.navigate(bookingUrl(target, s, room));
		}
	}

	clear(node).append(form);
	update();
}
